/**
 * 이상한 방 - 아이소메트릭 컷어웨이(단면 절개)
 *
 * 근거 문서: `이상한방-상태모델-v0.1.md` 원칙 2("시각적으로 찾을 수 없는 건 금지")
 * 데이터 원천: `render/roomShell.ts`의 섹션 메타(`SHELL_SECTIONS_KEY`)
 *
 * ## 이 모듈이 푸는 문제
 *
 * 아이소메트릭은 **고정 부감**이다. 20m × 20m 밀폐 아파트를 그대로 위에서 비추면
 * 카메라 쪽 외벽 두 면과 천장이 실내를 통째로 덮고, 내벽이 뒤쪽 구획을 가린다.
 * 그 상태의 "정답"은 찾을 수 있는 정답이 아니라 **존재하지 않는 정답**이며 원칙 2의 정면 위반이다.
 *
 * 그래서 카메라 방향에 따라 가리는 구조물을 걷어낸다. 걷어내는 규칙은 세 줄이다.
 *
 *  1. **천장** - 항상 숨긴다. 부감에서 천장이 남을 이유가 없다
 *  2. **외벽** - 카메라 쪽 두 면을 숨긴다 (dir별로 어느 두 면인지는 `NEAR_SIDES`)
 *  3. **내벽** - 허리 높이에서 자른 아래쪽만 남긴다. 자르는 것은 셸이 굽는 시점에 이미 끝나 있고
 *     (`roomShell.ts` 결정 5), 여기서는 카메라 쪽 면을 통째로 끄는 일만 한다
 *
 * ## 왜 내벽을 "지우지 않고 자르는가"
 *
 * 두 극단이 다 틀렸다. 내벽을 **다 남기면** 뒤쪽 구획이 사라지고(원칙 2 위반),
 * **다 지우면** 9구획이 하나의 큰 바닥으로 뭉개져 "여기가 주방이고 저기가 침실"이 읽히지 않는다.
 * 술래가 훑는 대상은 방이지 좌표가 아니므로, 구획감이 사라지면 탐색 전략 자체가 사라진다.
 *
 * 허리 아래를 남기면 둘 다 산다 - 경계는 띠로 읽히고 그 위로 시야가 통과한다.
 * 스툽이 가리는 바닥 띠(부감 35°에서 약 1.4m)는 **4방향 회전이 회수한다.**
 * 한 방향에서 스툽 뒤에 숨은 소품은 반대 방향(dir + 2)에서 정면으로 드러난다.
 * 회전이 선택 기능이 아니라 필수인 이유가 이것이다.
 *
 * ## 어떤 "방향"을 받아야 하는가 (중요)
 *
 * `update(dir)`에는 **카메라가 지금 실제로 서 있는 방향**을 넘겨야 한다
 * (`isoCamera.ts`의 `getVisualDirection()`). 회전 목표(`getDirection()`)를 넘기면
 * 카메라가 0.3초에 걸쳐 도는 동안 벽은 이미 도착지 기준으로 걷혀 있어,
 * **있어야 할 벽이 그 시간 내내 없다.** 개발자가 스크린샷으로 잡은 증상이 정확히 이것이다.
 *
 * 갈리는 지점이 회전의 중간(방위각 45°+90°k)인 것은 임의로 정한 값이 아니다.
 * 그 순간 교체되는 두 벽면은 시선과 정확히 평행해 **화면 투영 면적이 0**이라,
 * 어느 쪽을 켜고 꺼도 화면에 변화가 없다. 벽을 갈아치울 수 있는 유일하게 공짜인 순간이다.
 *
 * ## 성능 - 방향이 바뀔 때만 계산한다
 *
 * `update(dir)`는 같은 방향으로 다시 불리면 즉시 돌아간다. 프레임마다 도는 것은
 * 페이드가 진행 중일 때의 `advance(dt)`뿐이고, 그것도 끝나면 스스로 멈춘다.
 *
 * ## 페이드 - 왜 필요하고, 왜 이 정도면 충분한가
 *
 * 회전할 때 벽이 딱딱 사라지면 조잡하다. 다만 벽면 자체는 사실 **백페이스 컬링이 이미 완벽한 타이밍**으로
 * 처리한다 - 면이 앞/뒤로 뒤집히는 순간은 화면 투영 면적이 0인 순간이기 때문이다
 * (`roomShell.ts` 결정 6). 그래서 페이드는 컬링이 못 잡는 것(창틀 상자·걸레받이 윗면)을 위한 장치이고,
 * 벽면에 걸리는 페이드는 덤이다. 0.22초면 회전 연출에 묻힌다.
 *
 * 페이드 중에만 `transparent`를 켜고 끝나면 되돌린다. 반투명 머티리얼은 정렬 비용이 있고,
 * 완전히 숨길 것은 `visible = false`가 정답이다(드로우콜에서 아예 빠진다).
 *
 * ## 렌더 레이어 규약
 *
 * Three 객체의 제자리 변경을 허용한다(`scene.ts` 참조). 게임 상태는 읽지도 쓰지도 않는다.
 * 시간은 프레임 델타로만 흐른다 - 페이드는 순수 연출이라 시드 결정론과 무관하다.
 */

import * as THREE from 'three'

import type { FloorPlan, RoomSide } from '../core/floorPlan'
import { MS_PER_SECOND } from '../core/time'
import {
  SHELL_PLAN_ID_KEY,
  SHELL_SURFACE_KEY,
  forEachShellSection,
  isModeOwnedRole,
  isSectionVisibleForMode,
} from './roomShell'
import {
  ISO_AZIMUTH_BASE_RADIANS,
  ISO_DIRECTION_COUNT,
  ISO_QUARTER_TURN_RADIANS,
  isoOffsetDirection,
} from './scene'

/* --- 방향 규약 ------------------------------------------------------------------- */

/**
 * 4방향 회전 인덱스.
 *
 * `input/isoCamera.ts`가 같은 이름의 타입을 export 하지만, 여기서 다시 선언한다.
 * 이유는 의존 방향이다 - 렌더가 입력을 import 하면 셸 테스트가 카메라를 끌고 오게 된다.
 * 두 타입 모두 `0 | 1 | 2 | 3`이라 구조적으로 동일하므로 서로 그대로 주고받을 수 있다.
 */
export type IsoDirection = 0 | 1 | 2 | 3

/**
 * dir별 카메라가 서 있는 평면 코너의 부호. **`isoCamera.ts`와의 유일한 좌표 계약이다.**
 *
 * 아이소메트릭 카메라는 축이 아니라 **대각선 코너**에 선다. 축 방향에 서면 그 축에 수직인 벽이
 * 시선과 평행해져 두께 0인 벽면(결정 2)이 화면에서 소멸한다. 대각선이면 네 벽이 전부 45°로 보인다.
 *
 * ## 손으로 적은 표를 버린 이유 (회귀 주의)
 *
 * 예전에는 이 표를 상수 리터럴로 적어 뒀는데 **dir 1과 dir 3이 카메라와 반대로 돌아 있었다.**
 * `isoCamera.ts`는 방위각 `45° + dir × 90°`로 도므로 dir 1의 카메라는 (+x, −z)에 서는데,
 * 표는 (−x, +z)라고 적혀 있었다. 그래서 dir 1·3에서는 **앞을 가리는 벽 두 장이 그대로 남고
 * 뒤쪽 벽 두 장이 사라졌다** - "회전하면 있어야 할 벽이 없다"의 절반이 이것이었다.
 * 두 파일이 같은 사실을 각자 적어 두면 언젠가 갈라지므로, 이제 **카메라와 같은 식에서 유도한다.**
 * `scene.ts`를 import 하는 것은 같은 렌더 레이어라 의존 방향에 문제가 없다
 * (`input/isoCamera.ts`를 import 하는 것이 금지일 뿐이다).
 */
export const ISO_DIRECTION_CAMERA_CORNER: readonly Readonly<{ x: 1 | -1; z: 1 | -1 }>[] =
  Array.from({ length: ISO_DIRECTION_COUNT }, (_unused, dir) => cameraCornerOf(dir))

/**
 * dir에서 카메라가 서는 코너의 부호.
 * 성분이 전부 ±0.577이라 0 근처가 없다 - 부호만 뽑아도 부동소수점 경계 문제가 생기지 않는다.
 */
function cameraCornerOf(dir: number): Readonly<{ x: 1 | -1; z: 1 | -1 }> {
  const offset = isoOffsetDirection(ISO_AZIMUTH_BASE_RADIANS + dir * ISO_QUARTER_TURN_RADIANS)
  return { x: offset.x > 0 ? 1 : -1, z: offset.z > 0 ? 1 : -1 }
}

/**
 * dir별로 **카메라 쪽에 있는(= 화면 앞쪽을 가리는) 두 벽면**.
 *
 * 유도는 기계적이다. 셸의 'north' 면은 z가 최대인 쪽, 'east' 면은 x가 최대인 쪽에 있고
 * 노멀은 실내를 향한다(결정 1). 그러니 카메라가 +z 쪽에 있으면 'north'가, +x 쪽에 있으면 'east'가
 * 카메라를 등진다 - 코너 부호를 그대로 면 이름으로 옮기면 끝이다.
 * 위 표에서 파생시키므로 둘이 어긋날 수 있는 경로 자체가 없다.
 */
const NEAR_SIDES: readonly (readonly RoomSide[])[] = ISO_DIRECTION_CAMERA_CORNER.map((corner) => [
  corner.z === 1 ? 'north' : 'south',
  corner.x === 1 ? 'east' : 'west',
])

/** 방향 인덱스에 해당하는 근접면 두 개. 표가 비면 데이터 오류이므로 던진다. */
function nearSidesOf(dir: IsoDirection): readonly RoomSide[] {
  const sides = NEAR_SIDES[dir]
  if (sides === undefined) throw new RangeError(`아이소메트릭 방향이 0~3이 아니다: ${dir}`)
  return sides
}

/* --- 튜닝 값 --------------------------------------------------------------------- */

/**
 * 벽이 나타나고 사라지는 데 걸리는 시간(초). **회전 보간 시간의 절반이다.**
 *
 * 호출부가 `getVisualDirection()`(`isoCamera.ts`)으로 갱신하므로 벽이 갈리는 순간은
 * 회전의 **중간 지점**이다. 남은 회전 시간이 정확히 절반이니, 페이드도 그 절반에 맞추면
 * 카메라가 새 방향에 도착하는 순간 벽 교체도 같이 끝난다.
 *
 * 예전 값(0.22초)은 회전(0.3초)보다 길어서, 회전이 끝난 뒤에도 앞을 가리는 벽이
 * 반투명한 유령으로 0.07초쯤 남아 있었다. 그게 "벽이 사라진다"의 나머지 절반이다.
 * 이 값을 바꿀 때는 `isoCamera.ts`의 `ISO_ROTATE_DURATION_SECONDS`와의 관계를 같이 봐야 하며,
 * 그 관계는 `isoCamera.test.ts`가 상수로 고정해 두고 있다
 * (여기서 직접 import 하지 않는 것은 렌더가 입력을 의존하면 안 되기 때문이다).
 */
export const CUTAWAY_FADE_SECONDS = 0.15

/**
 * 페이드 한 프레임의 델타 상한(초).
 * 탭을 전환했다 돌아오면 rAF 간격이 수 초로 벌어지는데, 그 값을 그대로 흘리면
 * 페이드가 한 프레임에 끝나 결국 딱딱 끊긴다. `scene.ts`의 프레임 상한과 같은 이유다.
 */
const MAX_FADE_FRAME_SECONDS = 0.1

/* --- 머티리얼 상태 --------------------------------------------------------------- */

/** 컷어웨이가 건드리기 전의 머티리얼 상태. `dispose`가 여기로 되돌린다. */
type MaterialState = Readonly<{
  visible: boolean
  transparent: boolean
  opacity: number
  depthWrite: boolean
}>

function snapshotMaterial(material: THREE.Material): MaterialState {
  return {
    visible: material.visible,
    transparent: material.transparent,
    opacity: material.opacity,
    depthWrite: material.depthWrite,
  }
}

/**
 * `transparent` 토글은 셰이더 프로그램을 바꾸므로 `needsUpdate`가 필요하다.
 * 매 프레임 무조건 세우면 재컴파일이 돌아 프레임이 통째로 튄다 - 값이 실제로 바뀔 때만 세운다.
 */
function setTransparent(material: THREE.Material, transparent: boolean): void {
  if (material.transparent === transparent) return
  material.transparent = transparent
  material.needsUpdate = true
}

function restoreMaterial(material: THREE.Material, state: MaterialState): void {
  setTransparent(material, state.transparent)
  material.opacity = state.opacity
  material.depthWrite = state.depthWrite
  material.visible = state.visible
}

/* --- 페이드 대상 ----------------------------------------------------------------- */

/** 방향에 따라 껐다 켜는 섹션 하나. `alpha`가 목표에 닿으면 원래 머티리얼 상태로 되돌아간다. */
type FadingSection = {
  readonly material: THREE.Material
  readonly side: RoomSide
  readonly base: MaterialState
  alpha: number
  target: 0 | 1
}

/**
 * 현재 알파를 머티리얼에 반영한다.
 *
 * 끝점(0·1)에서는 **반드시 원래 상태로 되돌린다.** 페이드가 끝났는데 `transparent`가 켜져 있으면
 * 그 면은 영원히 반투명 정렬 큐에 남아 비용만 내고, `depthWrite`가 꺼진 채 남으면
 * 그 벽 뒤 소품이 벽을 뚫고 보인다 - 둘 다 조용히 누적되는 종류의 사고다.
 */
function applyFade(entry: FadingSection): void {
  const { material, base } = entry

  if (entry.alpha >= 1 || entry.alpha <= 0) {
    restoreMaterial(material, base)
    material.visible = entry.alpha >= 1
    return
  }

  material.visible = true
  setTransparent(material, true)
  material.opacity = base.opacity * entry.alpha
  // 사라지는 중인 벽이 깊이를 기록하면, 반투명해진 뒤에도 뒤쪽 구획을 계속 가려 구멍처럼 보인다.
  material.depthWrite = false
}

/* --- 공개 타입 ------------------------------------------------------------------- */

export type Cutaway = {
  /**
   * 카메라 방향 반영. 같은 방향으로 다시 부르면 아무 계산도 하지 않는다.
   *
   * **넘길 값은 `isoCamera.getVisualDirection()`이다** - 회전 목표가 아니라 지금 서 있는 방향.
   * 이유는 파일 상단 "어떤 방향을 받아야 하는가" 참고.
   */
  update(dir: IsoDirection): void
  /**
   * 페이드 진행(초). 계약에 없는 추가 메서드이며, 자체 rAF 대신 호출부 루프에서 몰아 돌리고 싶을 때 쓴다.
   * 이때는 `autoTick: false`로 만들어 두 주체가 같은 페이드를 두 번 진행시키지 않게 한다.
   */
  advance(dt: number): void
  /** 이 모듈이 건드린 모든 머티리얼·천장을 만들기 전 상태로 되돌린다. */
  dispose(): void
}

export type CutawayOptions = Readonly<{
  /** 페이드 길이(초). 0이면 즉시 전환 - 테스트와 저사양 모드용. */
  fadeSeconds?: number
  /**
   * 자체 `requestAnimationFrame` 루프로 페이드를 굴릴지. 기본 `true`.
   * rAF가 없는 환경(Node·테스트)에서는 자동으로 즉시 전환으로 떨어진다 -
   * 아무도 진행시키지 않아 반투명한 채로 멈추는 상태가 생기지 않게 하는 안전장치다.
   */
  autoTick?: boolean
}>

/* --- 조립 ----------------------------------------------------------------------- */

/** 셸 그룹에 심긴 평면도 id를 찾는다. 셸이 아닌 객체를 받았으면 `undefined`. */
function findStampedPlanId(root: THREE.Object3D): string | undefined {
  let found: string | undefined

  root.traverse((node) => {
    if (found !== undefined) return
    const stamped = node.userData[SHELL_PLAN_ID_KEY]
    if (typeof stamped === 'string') found = stamped
  })

  return found
}

function collectCeilings(root: THREE.Object3D): readonly THREE.Mesh[] {
  const ceilings: THREE.Mesh[] = []

  root.traverse((node) => {
    if (node instanceof THREE.Mesh && node.userData[SHELL_SURFACE_KEY] === 'ceiling') {
      ceilings.push(node)
    }
  })

  return ceilings
}

/**
 * 컷어웨이를 만든다. 만드는 즉시 아이소메트릭 상태(천장 없음 · 내벽 절단 · dir 0)로 셸을 맞춘다.
 *
 * 만들자마자 방향 0을 적용하는 이유: 첫 `update` 전까지 카메라 쪽 벽이 살아 있으면
 * 첫 프레임이 "벽만 가득한 화면"으로 나온다. 유효하지 않은 중간 상태를 아예 만들지 않는다.
 *
 * `plan`은 좌표 계산에 쓰지 않는다 - 방향과 벽면의 관계는 셸이 심어 둔 섹션 메타에 이미 들어 있다.
 * 대신 **셸이 정말 이 평면도로 구워졌는지** 확인하는 데 쓴다. 어긋난 조합은 화면에서
 * "왜 이 벽만 안 사라지지"로 나타나 원인 추적이 거의 불가능하므로 여기서 즉시 터뜨린다.
 */
export function createCutaway(
  shell: THREE.Object3D,
  plan: FloorPlan,
  options: CutawayOptions = {},
): Cutaway {
  const stampedPlanId = findStampedPlanId(shell)
  if (stampedPlanId === undefined) {
    throw new TypeError('컷어웨이에 넘긴 객체가 방 셸이 아니다 - 섹션 메타가 심겨 있지 않다')
  }
  if (stampedPlanId !== plan.id) {
    throw new RangeError(
      `셸은 평면도 '${stampedPlanId}'로 구워졌는데 컷어웨이에 '${plan.id}'가 들어왔다`,
    )
  }

  const fadeSeconds = Math.max(options.fadeSeconds ?? CUTAWAY_FADE_SECONDS, 0)
  const autoTick = options.autoTick ?? true
  const requestFrame = autoTick ? resolveFrameScheduler() : null

  // 페이드를 굴릴 주체가 없는데 페이드를 시작하면 반투명한 채로 영원히 멈춘다.
  // 자동 틱을 켰는데 rAF가 없는 환경(Node·테스트)이 정확히 그 경우이므로 즉시 전환으로 떨어뜨린다.
  const instant = fadeSeconds <= 0 || (autoTick && requestFrame === null)

  const fading: FadingSection[] = []
  /** 방향과 무관하게 모드만으로 정해지는 섹션(내벽 허리 위·벽마루). 되돌리기 위해 상태만 들고 있는다. */
  const modeOwned: { material: THREE.Material; base: MaterialState }[] = []

  forEachShellSection(shell, (material, meta) => {
    if (isModeOwnedRole(meta.role)) {
      modeOwned.push({ material, base: snapshotMaterial(material) })
      material.visible = isSectionVisibleForMode(meta.role, true)
      return
    }
    if (meta.side === null) return // 문틀·바닥처럼 어느 방향에서도 남는 것

    fading.push({ material, side: meta.side, base: snapshotMaterial(material), alpha: 1, target: 1 })
  })

  const ceilings = collectCeilings(shell).map((mesh) => ({ mesh, visible: mesh.visible }))
  for (const ceiling of ceilings) ceiling.mesh.visible = false

  let currentDir: IsoDirection | null = null
  let frameHandle: number | null = null
  let lastFrameMs: number | null = null
  let disposed = false

  function hasActiveFade(): boolean {
    return fading.some((entry) => entry.alpha !== entry.target)
  }

  function scheduleFrame(): void {
    if (requestFrame === null || frameHandle !== null || disposed) return
    frameHandle = requestFrame(onFrame)
  }

  function onFrame(timeMs: number): void {
    frameHandle = null
    if (disposed) return

    const elapsed = lastFrameMs === null ? 0 : (timeMs - lastFrameMs) / MS_PER_SECOND
    lastFrameMs = timeMs
    advance(Math.min(Math.max(elapsed, 0), MAX_FADE_FRAME_SECONDS))

    if (hasActiveFade()) scheduleFrame()
    else lastFrameMs = null
  }

  function advance(dt: number): void {
    if (dt <= 0 || disposed) return

    // fadeSeconds가 0이거나 rAF가 없으면 한 번에 끝낸다 - 중간 상태로 멈추는 경우를 만들지 않는다.
    const step = fadeSeconds > 0 ? dt / fadeSeconds : 1

    for (const entry of fading) {
      if (entry.alpha === entry.target) continue

      entry.alpha =
        entry.target === 1 ? Math.min(1, entry.alpha + step) : Math.max(0, entry.alpha - step)
      applyFade(entry)
    }
  }

  function applyDirection(dir: IsoDirection, snap: boolean): void {
    const near = nearSidesOf(dir)
    currentDir = dir

    for (const entry of fading) {
      const target = near.includes(entry.side) ? 0 : 1
      if (entry.target === target) continue

      entry.target = target
      if (!snap) continue

      entry.alpha = target
      applyFade(entry)
    }

    if (!snap) scheduleFrame()
  }

  function update(dir: IsoDirection): void {
    if (disposed) throw new Error('이미 dispose된 컷어웨이를 갱신하려 한다')
    // 성능 계약: 방향이 그대로면 아무것도 하지 않는다. 매 프레임 불려도 안전하다.
    if (dir === currentDir) return

    applyDirection(dir, instant)
  }

  function dispose(): void {
    if (disposed) return
    disposed = true
    // 예약된 rAF는 취소하지 않는다 - 콜백이 `disposed`를 보고 즉시 빠져나가고,
    // 그 사이 재예약도 같은 플래그가 막는다. 프레임 한 번 헛도는 것이 전부다.
    frameHandle = null

    for (const entry of fading) restoreMaterial(entry.material, entry.base)
    for (const entry of modeOwned) restoreMaterial(entry.material, entry.base)
    for (const ceiling of ceilings) ceiling.mesh.visible = ceiling.visible
  }

  // 첫 방향은 페이드 없이 박는다. 첫 프레임이 "벽이 녹는 중"으로 나올 이유가 없다.
  applyDirection(0, true)

  return { update, advance, dispose }
}

/**
 * 페이드를 굴릴 프레임 스케줄러. rAF가 없는 환경이면 `null`.
 * `autoTick: false`면 호출부가 `advance(dt)`로 직접 굴린다는 뜻이므로 아예 잡지 않는다.
 */
function resolveFrameScheduler(): ((cb: (t: number) => void) => number) | null {
  if (typeof globalThis.requestAnimationFrame !== 'function') return null

  return (cb) => globalThis.requestAnimationFrame(cb)
}
